'use client';

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/styleUtils';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import {
  backdropBaseClasses,
  backdropDefaultZ,
  sheetBaseClasses,
  sheetDefaultZ,
  headerClasses,
  titleClasses,
  closeButtonClasses,
  bodyClasses,
} from './BottomSheet.style';
import type { BottomSheetProps } from './BottomSheet.types';

export function BottomSheet({
  open,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  hideTitleBorder,
  hideHeader = false,
  className,
  bodyClassName,
  zIndex,
  sheetRef: sheetRefProp,
  initialFocusRef,
  dismissible = true,
  closeOnBackdrop = dismissible,
  'data-cy': dataCy = 'bottom-sheet',
  'data-cy-close': dataCyClose = 'bottom-sheet-close',
  'data-cy-backdrop': dataCyBackdrop = 'bottom-sheet-backdrop',
}: BottomSheetProps) {
  const tCommon = useTranslations('Common');
  const internalSheetRef = useRef<HTMLDivElement>(null);
  const sheetRef = sheetRefProp ?? internalSheetRef;
  useFocusTrap(sheetRef, open, { initialFocusRef });

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Close on Escape (skipped when the sheet is non-dismissible). A held key repeats keydown, so
  // repeats are ignored: onClose runs once per press, even when it navigates (PB-6948).
  useEffect(() => {
    if (!open || !dismissible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.repeat) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, dismissible]);

  if (!open) return null;

  const content = (
    <>
      {/* Backdrop — click-to-close only when dismissible and closeOnBackdrop */}
      <div
        className={cn(backdropBaseClasses, zIndex?.backdrop ?? backdropDefaultZ)}
        onClick={dismissible && closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
        data-cy={dataCyBackdrop}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={cn(sheetBaseClasses, zIndex?.sheet ?? sheetDefaultZ, className)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        data-cy={dataCy}
      >
        {/* Header — omitted entirely when hideHeader; close (X) hidden when non-dismissible */}
        {!hideHeader && (
          <div className={cn(headerClasses, hideTitleBorder && 'tw:border-b-0')}>
            <span className={titleClasses} data-cy="bottom-sheet-title">
              {title}
            </span>
            {dismissible && (
              <button
                type="button"
                className={closeButtonClasses}
                onClick={onClose}
                aria-label={tCommon('close')}
                data-cy={dataCyClose}
              >
                <MdClose size={24} aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={cn(bodyClasses, bodyClassName)}>{children}</div>

        {/* Footer */}
        {footer}
      </div>
    </>
  );

  return createPortal(content, document.body);
}

BottomSheet.displayName = 'BottomSheet';
